<template>
  <div
    v-on-clickaway="clickAway"
    :class="allClasses"
    v-bind="$attrs"
    v-on="$listeners"
    @blur.prevent="close"
    @keydown="startUsingKeyboard"
    @keyup="startUsingKeyboard"
    @keydown.prevent.arrow-up="arrowUp()"
    @keydown.prevent.arrow-down="arrowDown()"
    @keyup.home="navigateToStart()"
    @keyup.end="navigateToEnd()"
    @keyup.enter.space.stop.prevent="onEnter()"
    @keyup.escape="close()"
    @keydown.enter.space.stop.prevent="() => {}"
    @keypress="onTypeAhead($event.key)"
  >
    <EInput
      class="select__input"
      :as-button="!searchable"
      :value="selectedLabel"
      :placeholder="placeholder"
      :disabled="disabled"
      :invalid="invalid"
      :icon="inputIcon"
      icon-size="s"
      :role="searchable ? 'combobox' : null"
      :aria-autocomplete="searchable ? 'list' : null"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-label="ariaLabel"
      :aria-controls="$id('select-options')"
      :data-test-id="name"
      @mousedown="toggleOpen"
      @blur.prevent="onBlur"
      @keyup="onSearch"
    />
    <div
      v-if="isOpen"
      class="select__options"
    >
      <div
        :id="$id('select-options')"
        ref="options"
        class="select-options__list"
        role="listbox"
        :style="{ '--shown': optionsShown !== 'all' ? optionsShown - 0.5 : null }"
        data-test-id="select-options"
        @mousedown="onMousedown"
      >
        <EList>
          <EListItem
            v-for="(option, index) in opts"
            :key="option.value"
            :ref="`option-${index}`"
            :transition="false"
            :highlighted="usingKeyboard && isHighlighted(index)"
            class="select__option"
            :class="{ 'select__option--selected': isSelected(option.value) }"
            role="option"
            :aria-selected="isSelected(option.value)"
            :data-test-id="option.label"
            @click.stop="selectAndClose(option.value)"
          >
            <slot name="list-item" :option="option">
              {{ option.label }}
              <EIcon
                v-if="isSelected(option.value)"
                class="select__option-icon"
                icon="check"
                size="s"
              />
            </slot>
          </EListItem>
        </EList>
      </div>
    </div>
    <select
      :id="id"
      class="select__element"
      role="presentation"
      :aria-label="ariaLabel"
      tabindex="-1"
      :required="required"
      :disabled="disabled"
      :name="name"
      :value="selectedValue"
      @input.stop="select($event.target.value)"
    >
      <option v-if="placeholder" disabled value="" data-test-id="select-element-option">
        {{ placeholder }}
      </option>
      <option
        v-for="option in opts"
        :key="option.value"
        :value="option.value"
        data-test-id="select-element-option"
      >
        {{ option.label }}
      </option>
    </select>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

import { clamp } from '~~/common/js/universal/utils';
import classes from '~~/common/vue/mixins/classes';
import typeAhead from '~~/common/vue/mixins/type-ahead';
import EInput from '~~/common/vue/components/input';
import EIcon from '~~/common/vue/components/icon';
import EList from '~~/common/vue/components/list';
import EListItem from '~~/common/vue/components/list-item';

const stepFromDirection = (direction) => {
  switch (direction) {
    case 'up':
      return -1;
    case 'down':
      return 1;
    default:
      return 0;
  }
};

/**
 * An accessible `<select>` box compliant with WAI ARIA `listbox`
 */
export default {
  name: 'ESelect',
  components: {
    EInput,
    EIcon,
    EList,
    EListItem,
  },
  mixins: [classes, typeAhead, clickaway],
  props: {
    id: {
      type: [String, null],
      default: null,
    },
    value: {
      type: [String, Number, Boolean, Object],
      default: null,
    },
    name: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * The list of options in the select.
     */
    options: {
      type: Array,
      default: () => [],
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    optionsShown: {
      type: [Number, String],
      default: 4,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    noIcon: {
      type: Boolean,
      default: false,
    },
    mobileNative: {
      type: Boolean,
      default: true,
      cssClass: true,
    },
  },
  data() {
    return {
      highlightedIndex: null,
      selectedValue: this.value !== null ? this.value : '',
      usingKeyboard: false,
      // TODO: rename to 'open'
      isOpen: false,
      optionsClicked: false,
      dirty: false,
      searchTerm: this.value !== null ? this.options.find(
        (option) => option.value === this.value,
      )?.label : '',
    };
  },
  computed: {
    opts() {
      return this.options.map((option) => (typeof option !== 'object' ?
        { value: option, label: option } :
        option
      ));
    },
    valueToLabel() {
      return this.opts.reduce(
        (acc, { value, label }) => ({ ...acc, [value]: label }),
        {},
      );
    },
    selectedLabel() {
      // The ternary avoids the component glitching while searching.
      // Otherwise the component sometimes updates to null while typing.
      return this.searchable ? this.searchTerm : this.valueToLabel[this.selectedValue];
    },
    selectedIndex() {
      const index = this.opts.findIndex((option) => option.value === this.selectedValue);
      return index >= 0 ? index : null;
    },
    allClasses() {
      const allClasses = [...this.classes];
      if (this.isOpen && this.options.length > 0) {
        allClasses.push('select--open');
      }
      if (this.dirty) {
        allClasses.push('select--dirty');
      }
      return allClasses;
    },
    inputIcon() {
      if (this.noIcon) {
        return null;
      }

      return this.isOpen ? 'chevron-mini-up' : 'chevron-mini-down';
    },
  },
  watch: {
    value(val) {
      this.selectedValue = val;
    },
  },
  methods: {
    isSelected(value) {
      return this.selectedValue === value;
    },
    isHighlighted(index) {
      return this.highlightedIndex === index;
    },
    onMousedown() {
      this.optionsClicked = true;
    },
    onBlur() {
      if (!this.optionsClicked) {
        this.close();
      }
      this.optionsClicked = false;
    },
    toggleOpen(event) {
      if (!this.searchable) {
        event.preventDefault();
      }
      // When field is searchable, clicking on it shouldn't toggle,
      // but keep it open instead.
      if (!this.searchable && this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    },
    open() {
      if (!this.isOpen) {
        this.$emit('open');
      }
      if (!this.disabled) {
        this.isOpen = true;
      }
      if (this.highlightedIndex === null) {
        this.highlightedIndex = this.selectedIndex || 0;
      }
      // defer till next tick, because at this point the options won't have rendered yet
      this.$nextTick(() => this.navigate());
    },
    clickAway() {
      // This function avoids Jest errors
      // when it overrides the close() method.
      this.close();
    },
    close() {
      this.usingKeyboard = false;
      this.isOpen = false;
      this.highlightedIndex = this.selectedIndex || 0;
      this.$emit('close');
    },
    select(value) {
      this.dirty = true;
      this.selectedValue = value;
      this.searchTerm = this.valueToLabel[this.selectedValue];
      this.$emit('input', value);
    },
    selectAndClose(value) {
      this.select(value);
      this.close();
    },
    arrowUp() {
      // the user may have closed the dropdown with Esc
      // so we need to open it (without navigating up or down)
      if (!this.isOpen) {
        this.open();
        return;
      }
      this.$nextTick(() => this.navigate('up'));
    },
    arrowDown() {
      if (!this.isOpen) {
        this.open();
        return;
      }
      this.$nextTick(() => this.navigate('down'));
    },
    navigate(direction) {
      const step = stepFromDirection(direction);
      const nextIndex = this.highlightedIndex + step;
      this.highlightedIndex = clamp(nextIndex, 0, this.options.length - 1);
      const element = this.$refs[`option-${this.highlightedIndex}`]?.[0];
      // in unit tests, the ref isn't always retained after $nextTick
      if (element) {
        element.scrollIntoParentView();
      }
    },
    navigateToStart() {
      this.highlightedIndex = 0;
      this.navigate();
    },
    navigateToEnd() {
      this.highlightedIndex = this.options.length - 1;
      this.navigate();
    },
    onEnter() {
      if (!this.isOpen) {
        this.open();
      } else {
        this.selectCurrentOptionAndClose();
      }
    },
    selectCurrentOptionAndClose() {
      const value = this.opts[this.highlightedIndex]?.value;
      if (value) {
        this.selectAndClose(value);
      }
    },
    handleTypeAheadSequence(sequence) {
      const resultIndex = this.opts.findIndex(({ label }) => {
        const normalisedOption = label.replace(/\s/g, '').toLowerCase();
        return normalisedOption.startsWith(sequence);
      });

      this.highlightedIndex = resultIndex;
      this.navigate();
      this.typeAheadMemory = [];
    },
    startUsingKeyboard() {
      this.usingKeyboard = true;
    },
    onSearch(event) {
      if (this.searchable) {
        this.$emit('search', event.target.value);
        this.startUsingKeyboard();
        if (event.target.value) {
          this.open();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$icon-padding-right: $space-s;
// 4.5 because 4 options should be visually visible in their entirety + only half of the 5th one

.select {
  position: relative;
}

.select__options {
  @include elevation(1);

  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  overflow: hidden;
  background: var(--color-background-primary);
  border-left: $border-width solid var(--color-form-border-default);
  border-right: $border-width solid var(--color-form-border-default);
  border-bottom-left-radius: $border-radius-l;
  border-bottom-right-radius: $border-radius-l;
  // TODO: check if this can be turned into $z-index-3l (from 999 to 4)
  z-index: $z-index-overlay - 1;
}

.dark .select__options {
  // FE-666: temporary fix for dark-mode until Color System V2 is ready.
  background-color: var(--color-background-elevation-1);
  border-bottom: $border-width solid var(--color-form-border-default);
}

.select-options__list {
  max-height: calc(#{$select-option-height} * var(--shown));
  overflow: auto;
  position: relative;
}

.select__option {
  position: relative;

  &--selected {
    font-weight: $font-weight-1l;
    padding-right: $space-1l + $icon-padding-right;
  }
}

.select__option-icon {
  position: absolute;
  top: 50%;
  right: $icon-padding-right;
  transform: translateY(-50%);
  pointer-events: none;
}

.select__element {
  display: none;

  .select--mobile-native & {
    appearance: none;
    border: none;
    display: block;
    // the large font-size ensures mobile browsers
    // won't zoom in on the hidden <select>, because
    // it's font size is the same as body text
    font-size: 1rem;
    height: 100%;
    opacity: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: $z-index-l;

    @include tablet {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.select {
  .input__element {
    border: solid $border-width var(--color-form-border-default);

    &:hover {
      border-color: var(--color-form-border-hover);
    }

    &:focus {
      @extend %keyboard-nav-focus;

      border-color: var(--color-form-border-primary-active);
    }
  }

  &.select--open .input__element {
    // using the focus colour here because we want to convey the whole
    // component having focus (not just the options)
    border: solid $border-width var(--color-form-border-primary-active);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}
</style>
